/**
 * Shared card pieces.
 *
 * Every layout draws the same slots from the same model; they differ in which
 * pieces appear and how they are arranged. Keeping the pieces here means a
 * change to (say) the price row lands in all four layouts at once, while each
 * layout file stays a short, readable composition — the variant-per-file rule
 * without four copies of the same 200 lines.
 *
 * Pieces only. No layout decisions, no entry point.
 */

import { el, svg } from '../../dom.js';
import { renderCountdown } from '../countdown.js';
import { formatMoney } from '../../adapters/webshop.js';
import { STOCK_STATE } from '../../model.js';

/**
 * The wished/not-wished half of this class list isn't decoration — core's
 * own wishlist_action() (webshop/public/js/wishlist.js) reads
 * btn.find('.wish-icon').hasClass('wished') to decide whether a click means
 * add or remove. Without it every click here read as "not wished yet" and
 * only ever added, never removed - found live, this is the fix.
 */
function heartMarkup(wished) {
	return '<use class="wish-icon ' + (wished ? 'wished' : 'not-wished') + '" href="#icon-heart"></use>';
}

/* ── media ─────────────────────────────────────────────────────────────── */

const EYE = '<path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z"/><circle cx="12" cy="12" r="3"/>';

// Mirrors raftor_integrationhq's image_variants.py: VARIANT_WIDTHS + the
// exact `<stem>-{width}w.<ext>` naming convention. Every upload past that
// backend change generates these two siblings automatically; existing
// product photos need the one-time backfill
// (image_variants.backfill_variants) to have them FIRST.
//
// IMAGE_VARIANTS_ENABLED must stay false until that backfill has actually
// run and completed against the real bucket: verified live (see this
// session's srcset test) that a browser does NOT fall back to `src` when
// every srcset candidate 404s — it renders a fully broken image instead.
// Flipping this on before the backfill finishes would break every product
// photo on the storefront. Flip to true (and rebuild) only after confirming
// backfill_variants() has run clean.
const IMAGE_VARIANTS_ENABLED = false;
const IMAGE_VARIANT_WIDTHS = [400, 800];

/**
 * Card images are full camera-resolution originals (commonly 1200x1200+)
 * shown at ~170-220px — see this session's PWA/3G work for the measured
 * cost (~185KB average, ~90% of it wasted pixels). srcset lets the browser
 * pick the ~400w variant instead of downloading the original.
 *
 * data-full-src carries the untouched original forward for anything that
 * needs the real, full-resolution image — image_zoom_hover.js and
 * image_zoom_loader.js both read `img.currentSrc || img.src` to populate a
 * zoom/lightbox view, and `currentSrc` becomes whichever srcset candidate
 * the browser actually painted (often the small one) once srcset is
 * present. Both were updated to check data-full-src first — see their own
 * comments for why.
 */
function responsiveImageAttrs(src) {
	if (!IMAGE_VARIANTS_ENABLED || !src) return {};
	const lastDot = src.lastIndexOf('.');
	const lastSlash = src.lastIndexOf('/');
	if (lastDot <= lastSlash) return {}; // no extension to key variants off of

	const stem = src.slice(0, lastDot);
	const ext = src.slice(lastDot);
	const srcset = IMAGE_VARIANT_WIDTHS.map((w) => `${stem}-${w}w${ext} ${w}w`).join(', ');

	return {
		srcset,
		// Grid is ~2 columns under 576px (each card ~48vw), more columns
		// above that — see rfhq_product_card.css's .item-card flex-basis
		// rules. Approximate on purpose: sizes only steers which srcset
		// candidate downloads, being off by a few vw costs nothing.
		sizes: '(max-width: 575px) 48vw, (max-width: 991px) 31vw, 220px',
		'data-full-src': src,
	};
}

function renderMedia(model) {
	const inner = model.media.src
		? el('img', {
				class: 'card-img rfhq-pc-img',
				src: model.media.src,
				alt: model.media.alt,
				loading: 'lazy',
				decoding: 'async',
				itemprop: 'image',
				...responsiveImageAttrs(model.media.src),
		  })
		: el('div', { class: 'card-img-top no-image', text: model.media.fallbackInitials });

	const children = [
		el('a', { href: model.href, style: 'text-decoration: none;' }, [inner]),
	];

	// Every angle past the first, stacked and hidden - grid.js's hover
	// delegate (_bindDelegates) cycles through them via the
	// rfhq-pc-img--active class. Empty whenever gallery has one entry (the
	// common case today - see get_item_media's docstring on what populates
	// a second/third entry).
	model.media.gallery.slice(1).forEach((altSrc) => {
		children.push(
			el('img', {
				class: 'card-img rfhq-pc-img rfhq-pc-img--alt',
				src: altSrc,
				alt: '',
				'aria-hidden': 'true',
				loading: 'lazy',
				decoding: 'async',
				...responsiveImageAttrs(altSrc),
			})
		);
	});

	if (model.badges.length) {
		children.push(
			el(
				'div',
				{ class: 'ce-badges', 'data-rfhq-slot': 'badges' },
				model.badges.map((b) =>
					el('span', {
						class: ['ce-badge', b.tone ? `ce-badge-${b.tone}` : null],
						text: b.label,
					})
				)
			)
		);
	}

	// Zoom trigger. Deliberately its own control rather than the image: the
	// image is the link to the product page, and image_click_zoom.js excludes
	// .card-img-container for exactly that reason. data-zoom-enabled is also
	// avoided — its binder preventDefaults the whole target, which would
	// swallow the PDP navigation.
	if (model.media.src) {
		children.push(
			el(
				'button',
				{
					class: 'rfhq-pc-quickview',
					type: 'button',
					'aria-label': 'View larger image',
					dataset: { rfhqQuickview: model.media.src, rfhqQuickviewCaption: model.title },
				},
				[svg(EYE, { class: 'rfhq-pc-quickview__icon' })]
			)
		);
	}

	children.push(renderCountdown(model));

	return el('div', { class: 'card-img-container rfhq-pc-media' + (model.media.src ? '' : ' rfhq-pc-media--empty') }, children);
}

/* ── title row: title + floating wishlist/cart-qty ─────────────────────── */

function renderWishlist(model) {
	const wished = model.state.wished;
	return el(
		'div',
		{
			class: ['like-action', wished ? 'like-action-wished' : null, 'rfhq-pc-wish'],
			dataset: { itemCode: model.itemCode },
		},
		[svg(heartMarkup(wished), { class: 'icon sm' })]
	);
}

function renderCartIndicator(model) {
	return el('div', {
		class: ['cart-indicator', model.state.inCart ? null : 'hidden', 'rfhq-pc-cart-indicator'],
		dataset: { itemCode: model.itemCode },
		text: String(model.state.cartQty || 1),
	});
}

function renderTitleRow(model, ctx) {
	const children = [
		el('a', { href: model.href }, [
			el('div', { class: 'product-title rfhq-pc-title', itemprop: 'name', text: model.title }),
		]),
	];

	// Parity with grid.js: floating controls are suppressed for templates.
	if (model.actions.kind !== 'explore' && model.itemCode) {
		if (ctx.settings.enable_wishlist) children.push(renderWishlist(model));
		if (ctx.settings.enabled) children.push(renderCartIndicator(model));
	}

	return el('div', { class: 'rfhq-pc-top-row', style: 'margin-top: 1rem; display: flex;' }, children);
}

/* ── price ─────────────────────────────────────────────────────────────── */

function renderPrice(model, ctx) {
	// The slot wrapper is emitted unconditionally so patchSlot('price') always
	// has a target — an item can start with no price and gain one when a
	// variant is chosen. Only its *contents* are conditional. Same reasoning as
	// the stock slot.
	const slot = (children) =>
		el(
			'div',
			{
				class: 'product-price rfhq-pc-price-row',
				'data-rfhq-slot': 'price',
				itemprop: 'offers',
				itemscope: true,
				itemtype: 'https://schema.org/AggregateOffer',
			},
			children
		);

	// Block-level "Show Price" toggle — suppressed at render time, so the model
	// keeps the real price for anything else that needs it.
	if (ctx && ctx.showPrice === false) return slot([]);

	const p = model.price;
	if (p.current == null) return slot([]);
	// A price of 0 reads as the word the site chose (Website HQ Settings); with none chosen the card shows no price for it, as it always did.
	if (p.free) {
		const word = ctx && ctx.settings && ctx.settings.freePriceWord;
		return slot(word ? [el('span', { class: 'webhq-selling-price rfhq-pc-price rfhq-pc-price--free', text: word })] : []);
	}

	const children = [];
	if (p.isFrom) {
		// A template's price is the cheapest of its variants, so state that
		// rather than showing a bare number the shopper may not be able to buy at.
		children.push(el('span', { class: 'rfhq-pc-price-from', text: 'From ' }));
	}
	children.push(
		el('span', { class: 'webhq-selling-price rfhq-pc-price', text: p.currency + formatMoney(p.current) })
	);

	if (p.compare != null) {
		children.push(
			el('small', { class: 'striked-price rfhq-pc-price-compare' }, [
				el('s', { text: p.currency + formatMoney(p.compare) }),
			])
		);
		if (p.discountPct != null) {
			children.push(
				el('small', {
					class: 'ml-1 product-info-green rfhq-pc-discount',
					text: `${p.discountPct}% OFF`,
				})
			);
		}
	}

	return slot(children);
}

/* ── colour swatches / size pills ─────────────────────────────────────────
 * Interactive, on every card websitehq renders — the deciding factor is the
 * card itself (does the model have swatches/sizes to show), never which
 * grid structure happened to draw it. `ctx.owned` used to gate this off
 * entirely outside page-builder blocks, on the reasoning that raftor_webhq's
 * DOM-injection script (product_variant_swatches.js) would draw these
 * instead on any "unowned" grid (/all-products, Item Group pages) — but
 * websitehq owns every product card's pixels now, unconditionally; that
 * script still exists for surfaces it renders itself, and continues to
 * stand off any card websitehq draws (grid.js's data-rfhq-owned stamp is
 * unrelated to this and untouched).
 *
 * The markup keeps webhq's class contract
 * (.variant-attr-row[data-attribute] > .variant-pill[data-attribute][data-value])
 * so the existing CSS still applies and both systems agree on structure —
 * but pills are real buttons now, resolved via variant_resolver.js and
 * grid.js's own delegated click handler (card/render.js wires ctx.resolver
 * per card; the pieces here just need to emit the right element/attributes).
 */

function variantRow(attribute, slot, showLabel, pills) {
	return el('div', { class: 'variant-attr-row', 'data-attribute': attribute, 'data-rfhq-slot': slot || null }, [
		showLabel ? el('span', { class: 'variant-attr-label', text: attribute }) : null,
		el('div', { class: 'variant-pill-group' }, pills),
	]);
}

/** An attribute shown as Icons: the same pill contract as a colour swatch, the value's icon instead of a colour. The name is a tooltip on
 * hover and focus (rfhq_attribute_icons.css) and the accessible name. data-rfhq-icon keeps the page script from adding a second icon. */
function iconSwatch(s, attribute) {
	return el(
		'button',
		{
			type: 'button',
			class: 'variant-pill variant-pill--icon rfhq-pc-swatch',
			'data-attribute': s.attribute || attribute,
			'data-value': s.value,
			'data-rfhq-icon': '1',
			'data-rfhq-label': s.label,
			'aria-pressed': 'false',
			'aria-label': s.label,
		},
		[el('span', { class: 'rfhq-pill-icon', 'aria-hidden': 'true', style: `--rfhq-icon: url("${s.icon}")` })]
	);
}

function buildSwatchPills(model, attribute) {
	return model.swatches.map((s) =>
		s.icon ? iconSwatch(s, attribute) : el('button', {
			type: 'button',
			class: 'variant-pill variant-pill--color rfhq-pc-swatch',
			'data-attribute': s.attribute || attribute,
			'data-value': s.value,
			'aria-pressed': 'false',
			title: s.label,
			'aria-label': s.label,
			style: `--swatch-color: ${s.color}`,
		})
	);
}

function renderSwatches(model) {
	if (!model.swatches.length) return null;

	const attribute = model.swatches[0].attribute || 'Colour';
	return variantRow(
		attribute,
		'swatches',
		false, // a row of colours needs no "Colour" caption
		buildSwatchPills(model, attribute)
	);
}

/**
 * The dropdown alternate presentation (ctx.settings.sizePickerStyle ===
 * 'Dropdown'): colour swatches stay pill buttons, but sit in the same row as
 * a <select> for model.sizeDropdown.attribute, right-aligned via CSS
 * (.variant-attr-row--picker). The <select>'s own options are NOT filled in
 * here from model.sizes - they start empty/disabled and are patched in by
 * grid.js (patchSlot('sizeDropdown', ...)) once the bulk, stock-checked
 * fetch for the resolved colour returns; this file only ever renders
 * whatever model.sizeDropdown already holds, per THE RULE in render.js
 * (pieces fetch nothing).
 */
function renderSizeSelect(model) {
	const dd = model.sizeDropdown;
	// Until the stock check has come back the values the listing already knows are offered (as the button view shows them at once), and the stock check then takes the sold-out ones away:
	// the dropdown is never a dead "Loading" box that waits behind a queue of server calls.
	const known = dd.resolvedForColour ? dd.options : (model.sizes || []).filter((s) => s.attribute === dd.attribute).map((s) => ({ value: s.value, label: s.label || s.value }));
	const options = [
		el('option', {
			value: '',
			disabled: true,
			hidden: true,
			selected: !dd.selected,
			text: dd.resolvedForColour && !dd.options.length ? 'Out of stock' : 'Select ' + String(dd.attribute || 'size').toLowerCase(),
		}),
		...known.map((opt) =>
			el('option', { value: opt.value, selected: dd.selected === opt.value, text: opt.label })
		),
	];
	return el('div', { class: 'rfhq-pc-size-dropdown', 'data-rfhq-slot': 'sizeDropdown' }, [
		// Same caption class the button row uses for Size (variantRow's
		// showLabel branch) - a visible label here, not just the <select>'s
		// own aria-label, so the picker reads the same way in both
		// presentations rather than only being labelled for screen readers.
		el('span', { class: 'variant-attr-label', text: dd.attribute || 'Size' }),
		el('select', {
			class: 'variant-pill rfhq-pc-size variant-size-select',
			'data-attribute': dd.attribute,
			'aria-label': dd.attribute || 'Size',
			disabled: !known.length,
		}, options),
	]);
}

/** Swatches + the size dropdown, one row, swatches left / dropdown right. */
function renderVariantPickerRow(model, ctx) {
	const dropdownAttr =
		ctx && ctx.settings && ctx.settings.sizePickerStyle === 'Dropdown'
			? model.sizeDropdown && model.sizeDropdown.attribute
			: null;
	if (!dropdownAttr) return renderSwatches(model); // legacy button path, untouched

	if (!model.swatches.length && !model.sizes.length) return null;

	const attribute = (model.swatches[0] && model.swatches[0].attribute) || 'Colour';
	return el(
		'div',
		{ class: 'variant-attr-row variant-attr-row--picker', 'data-attribute': attribute, 'data-rfhq-slot': 'swatches' },
		[
			model.swatches.length ? el('div', { class: 'variant-pill-group' }, buildSwatchPills(model, attribute)) : null,
			renderSizeSelect(model),
		]
	);
}

/** Size (and any other non-colour attribute) as clickable pills. */
function renderSizes(model, ctx) {
	// The dropdown-picked attribute is drawn by renderVariantPickerRow instead
	// - drop it here so it isn't shown twice. Any OTHER non-colour attribute
	// (a 3rd attribute on the template) still renders as pills, unaffected.
	const dropdownAttr =
		ctx && ctx.settings && ctx.settings.sizePickerStyle === 'Dropdown'
			? model.sizeDropdown && model.sizeDropdown.attribute
			: null;
	const modelSizes = dropdownAttr
		? model.sizes.filter((s) => (s.attribute || 'Size') !== dropdownAttr)
		: model.sizes;
	if (!modelSizes.length) return null;

	const byAttribute = new Map();
	for (const size of modelSizes) {
		const key = size.attribute || 'Size';
		if (!byAttribute.has(key)) byAttribute.set(key, []);
		byAttribute.get(key).push(size);
	}

	const rows = [];
	for (const [attribute, values] of byAttribute) {
		rows.push(
			variantRow(
				attribute,
				null,
				true,
				values.map((v) =>
					el('button', {
						type: 'button',
						class: ['variant-pill', 'rfhq-pc-size', v.available ? null : 'variant-pill-oos'],
						'data-attribute': attribute,
						'data-value': v.value,
						'aria-pressed': 'false',
						disabled: v.available ? null : true,
						text: v.label,
					})
				)
			)
		);
	}
	// A single slot node wrapping every attribute row: patchSlot resolves one
	// target per slot name, so N sibling rows each claiming the name would make
	// the slot ambiguous.
	return el('div', { 'data-rfhq-slot': 'sizes' }, rows);
}

/* ── stock / offers / consumer discount ────────────────────────────────── */

const STOCK_CLASS = {
	[STOCK_STATE.OUT]: 'out-of-stock rfhq-pc-stock rfhq-pc-stock--out',
	[STOCK_STATE.BACKORDER]: 'out-of-stock rfhq-pc-stock rfhq-pc-stock--backorder',
	[STOCK_STATE.LOW]: 'rfhq-pc-stock rfhq-pc-stock--low',
	// Plain "in stock" never carried a label before dual_stock existed
	// (adaptStock always returned '' for this state otherwise) -- without
	// this entry, `cls` below is undefined and the whole `label && cls`
	// check silently drops a real, non-empty dual_stock label. Found live:
	// the resolver and the adapter both returned the correct label, but
	// nothing rendered because this map had no IN entry to look up.
	[STOCK_STATE.IN]: 'rfhq-pc-stock rfhq-pc-stock--in',
};

function renderStock(model) {
	// The wrapper is always emitted so patchSlot('stock') has something to
	// target even when the card currently shows no stock line.
	const cls = STOCK_CLASS[model.stock.state];
	const inner =
		model.stock.label && cls
			? el('span', { class: `${cls} mb-2 mt-1`, text: model.stock.label })
			: null;
	return el('div', { 'data-rfhq-slot': 'stock' }, [inner]);
}

function renderConsumerDiscount(model) {
	const value = Number(model.consumerDiscount);
	if (!isFinite(value) || value <= 0) return null;
	return el('div', { class: 'customer-discount-info', text: `${value}% Consumer Offer` });
}

/** "Ends Sep 6" - informational, not the badge countdown's ticking clock;
 * a stack of offer lines each running their own timer would be noisy. */
function formatOfferEndDate(endDate) {
	const ts = Date.parse(endDate);
	if (!isFinite(ts)) return '';
	return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(new Date(ts));
}

function renderOffers(model) {
	if (!model.offers.length) return null;
	// `.offer-container` is deliberate, not decoration: raftor_webhq's
	// product_offers.js returns early on any card that already has one, the
	// same stand-down check badges.js makes via `.ce-badges`. Declared once,
	// on the outer wrapper.
	return el('div', { class: 'mt-2 offer-container rfhq-pc-offers' }, [
		el('div', { class: 'offers-heading mb-2' }, [el('b', { text: 'Available Offers' })]),
		el(
			'div',
			{ class: 'rfhq-pc-offer-list' },
			model.offers.map((o) => {
				const endLabel = o.endDate ? formatOfferEndDate(o.endDate) : '';
				return el('div', { class: 'offer-line mt-2 d-flex' }, [
					el('p', { class: 'offer-line-text mb-0' }, [
						o.subtitle ? `${o.title}: ${o.subtitle}` : o.title,
						endLabel ? el('span', { class: 'offer-line-expiry', text: `Ends ${endLabel}` }) : null,
					]),
				]);
			})
		),
	]);
}


/** Brand line. */
function renderEyebrow(model) {
	if (!model.eyebrow) return null;
	return el('div', { class: 'brand-container mb-1' }, [
		el('span', { class: 'brand-badge rfhq-pc-eyebrow', text: model.eyebrow }),
	]);
}

/** Item group line. */
function renderCategory(model) {
	if (!model.category) return null;
	return el('div', { class: 'product-category rfhq-pc-category', itemprop: 'name', text: model.category });
}

const STAR = '<use href="#icon-star"></use>';

/**
 * Five stars, laid out twice and stacked: a muted "track" row underneath, a
 * gold "fill" row on top clipped to the rating's percentage of the full
 * 5-star width (Amazon's own technique) — continuous, not rounded to the
 * nearest half star, so 4.3 and 4.7 read as visibly different amounts.
 */
function renderStars(value) {
	const pct = Math.max(0, Math.min(1, value / 5)) * 100;
	const fiveStars = () =>
		Array.from({ length: 5 }, () => svg(STAR, { class: 'icon sm rfhq-pc-star' }));
	return el('div', { class: 'rfhq-pc-stars' }, [
		el('div', { class: 'rfhq-pc-stars-track' }, fiveStars()),
		el('div', { class: 'rfhq-pc-stars-fill', style: `width: ${pct}%` }, fiveStars()),
	]);
}

/**
 * Aggregate rating line. Renders nothing when there is no review data — same
 * "nothing to show" convention as renderEyebrow/renderConsumerDiscount — since
 * most items have zero reviews and an empty star row would be worse than none.
 */
function renderRating(model) {
	if (model.rating.value == null || !model.rating.count) return null;
	return el('div', { class: 'rfhq-pc-rating' }, [
		renderStars(model.rating.value),
		el('span', { class: 'rfhq-pc-rating-value', text: model.rating.value.toFixed(1) }),
		el('span', { class: 'rfhq-pc-rating-count', text: `(${model.rating.count})` }),
	]);
}

/**
 * The actions slot. `renderAction` is injected rather than imported so this
 * module stays free of the action registry — pieces know nothing about kinds.
 */
function renderActionsSlot(model, ctx, renderAction) {
	return el('div', { class: 'cart-action-container rfhq-pc-actions', 'data-rfhq-slot': 'actions' }, [
		renderAction(model, ctx),
	]);
}

export {
	renderMedia,
	renderEyebrow,
	renderCategory,
	renderRating,
	renderActionsSlot,
	renderWishlist,
	renderCartIndicator,
	renderTitleRow,
	renderPrice,
	renderSwatches,
	renderVariantPickerRow,
	renderSizes,
	renderStock,
	renderConsumerDiscount,
	renderOffers,
	variantRow,
};
